import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV_LINKS, BRANDS, COLLECTIONS } from "../data";
import { BrandInline, Wordmark, Logo } from "./Brand";

const COLLECTION_MAP = {
  "Premium Spices": "agraga",
  "Specialty Coffee": "agraga",
  "Wellness": "vanshve",
  "Beauty": "charbelle",
  "Heritage Crafts": "artibelle",
  "GI Products": "artibelle",
};

export default function Navbar({ onQuote }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        data-testid="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md border-b border-[#E7E1D6]"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto h-[76px] md:h-[88px] px-6 md:px-10 flex items-center justify-between">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            data-testid="nav-logo"
            className="flex items-center"
          >
            <BrandInline
              color={scrolled ? "#111" : "#F8F7F3"}
              emblem="w-10 h-10"
              glow={!scrolled}
              className="transition-colors duration-500"
            />
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className={`link-underline text-sm font-body transition-colors duration-500 ${
                  scrolled ? "text-[#333] hover:text-[#111]" : "text-white/90 hover:text-white"
                }`}
              >
                {l.label}
              </button>
            ))}

            {/* Brands dropdown */}
            <div className="relative group">
              <button
                data-testid="nav-brands"
                className={`inline-flex items-center gap-1 text-sm font-body transition-colors duration-500 ${
                  scrolled ? "text-[#333] hover:text-[#111]" : "text-white/90 hover:text-white"
                }`}
              >
                Brands <ChevronDown size={14} className="transition-transform duration-300 group-hover:rotate-180" />
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                <div className="bg-white border border-[#E7E1D6] shadow-xl py-2 min-w-[200px]">
                  {BRANDS.map((b) => (
                    <Link
                      key={b.key}
                      to={`/brands/${b.key}`}
                      data-testid={`nav-brand-${b.key}`}
                      className="block px-5 py-2.5 font-head text-sm text-[#333] hover:bg-[#F8F7F3] hover:text-[#B08D57] transition-colors"
                    >
                      {b.name.charAt(0) + b.name.slice(1).toLowerCase()}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Collections dropdown */}
            <div className="relative group">
              <button
                data-testid="nav-collections"
                className={`inline-flex items-center gap-1 text-sm font-body transition-colors duration-500 ${
                  scrolled ? "text-[#333] hover:text-[#111]" : "text-white/90 hover:text-white"
                }`}
              >
                Collections <ChevronDown size={14} className="transition-transform duration-300 group-hover:rotate-180" />
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                <div className="bg-white border border-[#E7E1D6] shadow-xl py-2 min-w-[210px]">
                  {COLLECTIONS.map((c) => (
                    <Link
                      key={c}
                      to={`/brands/${COLLECTION_MAP[c] || "agraga"}`}
                      className="block px-5 py-2.5 font-head text-sm text-[#333] hover:bg-[#F8F7F3] hover:text-[#B08D57] transition-colors"
                    >
                      {c}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link
              to="/founder"
              data-testid="nav-link-founder"
              className={`link-underline text-sm font-body transition-colors duration-500 ${
                scrolled ? "text-[#333] hover:text-[#111]" : "text-white/90 hover:text-white"
              }`}
            >
              Founder
            </Link>
            <button
              data-testid="nav-quote-btn"
              onClick={onQuote}
              className="rounded-full bg-[#111] text-white text-sm font-head font-semibold px-7 py-3 transition-colors duration-300 hover:bg-[#B08D57]"
            >
              Request a Quote
            </button>
          </nav>

          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(true)}
            className={`lg:hidden ${scrolled ? "text-[#111]" : "text-white"}`}
          >
            <Menu size={26} strokeWidth={1.5} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] bg-[#111] flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="h-[76px] px-6 flex items-center justify-between">
              <span className="flex items-center gap-3">
                <Logo className="w-9 h-9" glow />
                <Wordmark color="#F8F7F3" />
              </span>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="text-white">
                <X size={26} strokeWidth={1.5} />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center gap-2 px-8">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  onClick={() => go(l.id)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.06 }}
                  className="font-display text-[#F8F7F3] text-4xl py-3 text-left border-b border-white/10"
                >
                  {l.label}
                </motion.button>
              ))}
              <Link
                to="/founder"
                onClick={() => setOpen(false)}
                className="font-display text-[#F8F7F3] text-4xl py-3 text-left border-b border-white/10"
              >
                Founder
              </Link>
              <p className="overline text-[#B08D57] mt-6 mb-1">Brands</p>
              {BRANDS.map((b) => (
                <Link
                  key={b.key}
                  to={`/brands/${b.key}`}
                  onClick={() => setOpen(false)}
                  className="font-display text-[#F8F7F3]/90 text-2xl py-2 text-left"
                >
                  {b.name.charAt(0) + b.name.slice(1).toLowerCase()}
                </Link>
              ))}
              <button
                onClick={() => { setOpen(false); onQuote(); }}
                className="mt-8 w-full rounded-full bg-[#B08D57] text-white font-head font-semibold py-4"
              >
                Request a Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
